import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { withApiPrefix } from '../../services/api';
import styles from '../study/StudySentencesPage.module.css';
import { AlertIcon, EditIcon } from '../../components/Icons';
import LoadingSpinner from '../../components/LoadingSpinner';
import StudyHeader from '../study/StudyHeader';
import SentenceCard from '../reader/components/SentenceCard/SentenceCard';
import useAudioClipPlayer from '../../hooks/useAudioClipPlayer';

const BookSentencesListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookId = searchParams.get('bookid') || searchParams.get('bookId');

  const [sentences, setSentences] = useState([]);
  const [sentencesLoading, setSentencesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chapterMetadata, setChapterMetadata] = useState({});
  const [collapsedChapters, setCollapsedChapters] = useState({});
  const [favoriteLoading, setFavoriteLoading] = useState({});

  const { playingClip, playSentenceClip, stopClip } = useAudioClipPlayer();

  const loadSentencesByBook = useCallback(async (targetBookId) => {
    if (!targetBookId) {
      setSentences([]);
      setError('缺少书籍ID');
      return;
    }
    try {
      setSentencesLoading(true);
      setError(null);
      const response = await api.get(withApiPrefix(`favorite-sentences/by-book/${targetBookId}`));
      const items = Array.isArray(response.data) ? response.data : [];
      setSentences(items);
      if (!items.length) {
        setError('该书没有收藏的句子');
      }
    } catch (err) {
      console.error('加载收藏句子失败:', err);
      setError('加载收藏句子失败，请稍后重试');
    } finally {
      setSentencesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSentencesByBook(bookId);
  }, [bookId, loadSentencesByBook]);

  // Fetch chapter metadata for grouping
  const pendingChapterIds = useMemo(() => {
    const ids = new Set();
    sentences.forEach((s) => {
      if (s.chapter_id != null) {
        const key = String(s.chapter_id);
        if (!chapterMetadata[key]) ids.add(key);
      }
    });
    return Array.from(ids);
  }, [sentences, chapterMetadata]);

  useEffect(() => {
    if (!pendingChapterIds.length) return;
    let cancelled = false;
    const load = async () => {
      const results = await Promise.allSettled(
        pendingChapterIds.map(async (chId) => {
          const res = await api.get(`/chapters/${chId}`);
          const ch = res.data;
          let displayTitle = ch.title;
          try {
            const parsed = JSON.parse(ch.content);
            if (parsed?.chapter_title) displayTitle = parsed.chapter_title;
          } catch (_) { /* ignore */ }
          return {
            chId,
            data: {
              title: displayTitle || ch.title,
              orderIndex: ch.order_index,
              volumeTitle: ch.volume?.title || '',
              volumeOrder: ch.volume?.order_index ?? Number.MAX_SAFE_INTEGER,
            },
          };
        })
      );
      if (cancelled) return;
      const entries = {};
      results.forEach((r) => {
        if (r.status === 'fulfilled') entries[r.value.chId] = r.value.data;
      });
      if (Object.keys(entries).length) {
        setChapterMetadata((prev) => ({ ...prev, ...entries }));
      }
    };
    load();
    return () => { cancelled = true; };
  }, [pendingChapterIds]);

  const chapterGroups = useMemo(() => {
    const map = new Map();
    sentences.forEach((s) => {
      const chId = s.chapter_id != null ? String(s.chapter_id) : '__unknown__';
      if (!map.has(chId)) map.set(chId, []);
      map.get(chId).push(s);
    });
    return Array.from(map.entries())
      .map(([chId, items]) => {
        const meta = chId !== '__unknown__' ? chapterMetadata[chId] : null;
        return {
          chapterId: chId,
          title: meta?.title || (chId !== '__unknown__' ? `Chapter ${chId}` : '未知章节'),
          orderIndex: meta?.orderIndex ?? Number.MAX_SAFE_INTEGER,
          volumeOrder: meta?.volumeOrder ?? Number.MAX_SAFE_INTEGER,
          sentences: items,
        };
      })
      .sort((a, b) => {
        if (a.volumeOrder !== b.volumeOrder) return a.volumeOrder - b.volumeOrder;
        return a.orderIndex - b.orderIndex;
      });
  }, [sentences, chapterMetadata]);

  const handleDeleteSentence = async (sentence) => {
    const sentenceId = sentence?.id ?? sentence?.favorite_id ?? sentence?.favoriteId;
    if (!sentenceId) return;
    setFavoriteLoading((prev) => ({ ...prev, [sentenceId]: true }));
    try {
      await api.delete(withApiPrefix(`favorite-sentences/${sentenceId}`));
      setSentences((prev) => prev.filter((item) => item.id !== sentenceId));
    } catch (err) {
      console.error('删除收藏句子失败:', err);
      alert('删除失败，请稍后重试');
    } finally {
      setFavoriteLoading((prev) => {
        const next = { ...prev };
        delete next[sentenceId];
        return next;
      });
    }
  };

  const handleStartSentenceStudy = useCallback(() => {
    if (!bookId) return;
    navigate(`/learn/sentences?bookid=${encodeURIComponent(bookId)}`, {
      state: {
        from: 'historySentences',
        historyReturn: { activeView: 'sentences' },
        sentences,
        bookId,
      },
    });
  }, [navigate, sentences, bookId]);

  if (!bookId) {
    return (
      <div className={styles.study}>
        <div className={styles.noHistory}>
          <div className={styles.noHistoryIcon}><AlertIcon size={48} /></div>
          <h3>缺少书籍ID</h3>
          <p>请从收藏句子列表选择书籍后查看</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.study}>
      <div className={styles.detailView}>
        <StudyHeader title="收藏句子" onBack={() => navigate('/study/sentences')}>
          <button type="button" className={styles.studyBtn} onClick={handleStartSentenceStudy}>
            去学习
          </button>
        </StudyHeader>
        <div className={styles.detailContent}>
          {sentencesLoading ? (
            <div className={styles.noHistory}>
              <LoadingSpinner text="加载收藏句子中..." />
            </div>
          ) : error && !sentences.length ? (
            <div className={styles.noHistory}>
              <div className={styles.noHistoryIcon}><AlertIcon size={48} /></div>
              <p>{error}</p>
            </div>
          ) : !sentences.length ? (
            <div className={styles.noHistory}>
              <div className={styles.noHistoryIcon}><EditIcon size={48} /></div>
              <h3>还没有收藏的句子</h3>
              <p>在阅读时点击句子前的收藏按钮即可收藏句子</p>
            </div>
          ) : (
            <div className={styles.chapterGroupsContainer}>
              {chapterGroups.map((group) => {
                const isCollapsed = !!collapsedChapters[group.chapterId];
                return (
                  <div key={group.chapterId} className={styles.chapterGroup}>
                    <button
                      type="button"
                      className={styles.chapterGroupHeader}
                      onClick={() =>
                        setCollapsedChapters((prev) => ({
                          ...prev,
                          [group.chapterId]: !prev[group.chapterId],
                        }))
                      }
                    >
                      <span className={styles.chapterGroupTitle}>{group.title}</span>
                      <span className={styles.chapterGroupRight}>
                        <span className={styles.chapterGroupCount}>{group.sentences.length} 句</span>
                        <span
                          className={`material-icons-round ${styles.chapterGroupArrow}`}
                          style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                        >
                          expand_more
                        </span>
                      </span>
                    </button>
                    {!isCollapsed && (
                      <div className={styles.sentenceCardList}>
                        {group.sentences.map((sentence) => {
                          const sid = sentence.id ?? sentence.favorite_id ?? sentence.favoriteId ?? sentence.sentence_id;
                          return (
                            <SentenceCard
                              key={sid}
                              sentence={sentence}
                              favoriteLoading={!!favoriteLoading[sid]}
                              onFavoriteToggle={handleDeleteSentence}
                              playingClip={playingClip}
                              onPlayClip={playSentenceClip}
                              onStopClip={(lang) => stopClip(lang, true)}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookSentencesListPage;
